<script setup lang="ts">
import { debounce, isNumber } from 'lodash-es';
import {
  ref,
} from 'vue';

import type { ESmartFilterCompareMarks } from '@modules/smart-filters/enums';
import VIconButton from '@components/buttons/VIconButton/VIconButton.vue';
import VFormNumberInput
  from '@modules/forms/components/inputs/VFormNumberInput/VFormNumberInput.vue';
import VFormSelectInput
  from '@modules/forms/components/inputs/VFormSelectInput/VFormSelectInput.vue';
import { FORM_INPUT_DEBOUNCE_DEFAULT_MS } from '@modules/forms/constants';
import { TRANSLATION_GROUP_SMART_FILTERS } from '@modules/smart-filters/constants';
import { QA_TABLES } from '@modules/tables/constants';
import { watchImmediate } from '@vueuse/core';

import type { ISmartFilterCompareRowEmits, ISmartFilterCompareRowProps } from './types';

const props = defineProps<ISmartFilterCompareRowProps>();
const emit = defineEmits<ISmartFilterCompareRowEmits>();

const innerMark = ref<ESmartFilterCompareMarks>();
const innerNumber = ref<number>();

function emitChange(): void {
  emit('change', {
    mark: innerMark.value || null,
    value: innerNumber.value ?? null,
  });
}

function onNumberChange(value?: number | null): void {
  if (isNumber(value)) {
    innerNumber.value = value;
  }

  emitChange();
}

function onDropdownChange(value?: ESmartFilterCompareMarks): void {
  innerMark.value = value;

  emitChange();
}

const debouncedChange = debounce(onNumberChange, FORM_INPUT_DEBOUNCE_DEFAULT_MS);

function onBlur(): void {
  debouncedChange.flush();
}

function onClear(): void {
  innerMark.value = undefined;
  innerNumber.value = undefined;
  emitChange();
}

watchImmediate(() => props.markValue, (newValue) => {
  innerMark.value = newValue;
});
watchImmediate(() => props.numberValue, (newValue) => {
  innerNumber.value = newValue;
});
</script>

<template>
  <div v-data-qa-auto-id="'VSmartFilterCompareRow'" class="flex items-center gap-4">
    <div class="flex grow flex-col gap-2 sm:mt-0 sm:flex-row">
      <div
        v-data-test="{ el: QA_TABLES.filterCompareRowCondition }"
        class="w-full sm:w-1/2"
      >
        <VFormSelectInput
          :name="markName"
          is-full-width
          :label="$getTranslation(TRANSLATION_GROUP_SMART_FILTERS, 'SEGMENTS_COMPARE_CONDITION_SELECTOR')"
          :disabled="isSupervision"
          :options="options"
          :value="innerMark"
          @change="onDropdownChange"
        />
      </div>
      <div
        v-data-test="{ el: QA_TABLES.filterCompareRowValue }"
        class="w-full sm:w-1/2"
      >
        <VFormNumberInput
          is-full-width
          :name="numberName"
          :value="innerNumber"
          :label="$getTranslation(TRANSLATION_GROUP_SMART_FILTERS, 'SEGMENTS_COMPARE_COMPARE_VALUE')"
          :disabled="isSupervision"
          :max-fraction-digits="0"
          :use-grouping="false"
          @input="debouncedChange"
          @blur="onBlur"
        />
      </div>
    </div>
    <div class="mb-4">
      <VIconButton
        v-data-test="{ el: QA_TABLES.filterCompareRowClear }"
        icon="pi pi-times"
        class="flex-none"
        :disabled="disableClearButton || isSupervision"
        @click="onClear"
      />
    </div>
  </div>
</template>
